import type { Page, ReceiptListQuery } from '@chukua/common';
import { Table, type TableColumnsType } from 'antd';
import { Receipt, SearchX } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { ListSearch } from '../../../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { isReceiptQueryFiltered } from '../policies/receipt-filters';
import type { ReceiptView } from '../receipts.api';
import { ReceiptFilters } from './ReceiptFilters';

interface ReceiptsTableProps {
  page: Page<ReceiptView>;
  columns: TableColumnsType<ReceiptView>;
  list: ListQueryControls<ReceiptListQuery>;
  isRefreshing: boolean;
  /** Changes what the empty state says when nothing has been recorded yet. */
  canRecord: boolean;
}

/** Search, filters and the server-paged table; the old rows stay up, dimmed, while the next page loads. */
export function ReceiptsTable({ page, columns, list, isRefreshing, canRecord }: ReceiptsTableProps) {
  const emptyText = isReceiptQueryFiltered(list.query) ? <NoMatchingReceipts /> : <NoReceiptsYet canRecord={canRecord} />;

  return (
    <div className="flex flex-col gap-4">
      <ListSearch value={list.query.q} placeholder="Search reference, payer or customer" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
      <ReceiptFilters list={list} />
      <Table
        rowKey="id"
        dataSource={page.items}
        columns={columns}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText }}
        {...list.tableProps<ReceiptView>(page.total)}
      />
    </div>
  );
}

function NoMatchingReceipts() {
  return <EmptyState icon={SearchX} title="No matching receipts" description="No receipts match this search or these filters." />;
}

function NoReceiptsYet({ canRecord }: { canRecord: boolean }) {
  return (
    <EmptyState
      icon={Receipt}
      title="No receipts yet"
      description={canRecord ? 'Record the first payment a customer has made.' : 'Payments customers make will appear here once Finance records them.'}
    />
  );
}
